import { useEffect, useState } from "react";

const breakpoints: Record<string, string> = {
  sm: "(max-width: 767px)",
  md: "(min-width: 768px) and (max-width: 1023px)",
  lg: "(min-width: 1024px)",
};

/**
 * Custom hook to check if the current viewport matches a given breakpoint.
 * Returns true if the viewport width is within the specified breakpoint range.
 *
 * WARNING: SSR Hydration Mismatch Risk
 * -------------------------------------
 * This hook initialises with `false` on the server (no `window` available in
 * Cloudflare Workers / SSR). When the client hydrates on a device that *does*
 * match the query, the first render will differ from the server-rendered HTML,
 * causing a flash / layout shift (CLS).
 *
 * Prefer CSS-only responsive patterns (Tailwind breakpoints, `hidden md:block`,
 * CSS media queries) whenever possible. Only use this hook for behaviours that
 * genuinely cannot be expressed in CSS (e.g. conditionally mounting heavy
 * third-party widgets).
 */
export default function useMediaQuery(breakpoint: string): boolean {
  const query = breakpoints[breakpoint] ?? breakpoint;
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
